
import React, { useEffect, useState, useRef } from 'react';
import { Card, Button, Badge } from '../components/UI';
import { apiRequest, apiRequestFull } from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { useLeague } from '../components/LeagueContext';
import { Trophy, Save, RefreshCw, Lock, Unlock, Calendar, Flag, Search, X, ChevronRight, ChevronsRight, Hourglass, Users, Info, CheckCircle, XCircle, GripVertical, History, ArrowUp, ArrowDown, Eye, ShieldAlert, Terminal, List, LayoutGrid, Bug, AlertTriangle } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { UserRole } from '../types';
import { PageHero } from '../components/PageHero';

interface Prediction {
    picks: (string | null)[];
    sprint_picks: (string | null)[];
    points: number | null;
    sprint_points: number | null;
    locked: boolean;
}

interface LeaderboardEntry {
    username: string;
    avatar_url?: string;
    total_score: number;
    tips_count: number;
}

interface Participant {
    user_id: string; 
    username: string;
    avatar_url?: string;
    team_color?: string;
    team_name?: string;
    last_points?: number;
    points?: number | null;
    sprint_points?: number | null;
    picks?: (string | null)[]; 
    sprint_picks?: (string | null)[];
}

interface HistoryItem {
    id: number;
    round_number: number;
    location: string;
    track_name: string;
    date_time: string;
    picks: (string | null)[];
    sprint_picks: (string | null)[];
    points: number | null;
    sprint_points: number | null;
}

interface RealResult {
    position: number;
    sprint_position?: number;
    user_id: string;
}

interface LogItem {
    created_at: string;
    username: string;
    action: string;
    track_location: string;
    round_number: number;
}

// Helper for formatting date safely
const formatDateSafe = (dateString: string) => {
    if (!dateString) return 'Unbekannt';
    const safeStr = dateString.replace(' ', 'T');
    const date = new Date(safeStr);
    if (isNaN(date.getTime())) return dateString; 
    return date.toLocaleString('de-DE');
};

const DriverPoolItem: React.FC<{ driver: any, attendance?: string, onClick: () => void }> = ({ driver, attendance, onClick }) => (
    <div onClick={onClick} className="flex items-center gap-2 p-2 rounded bg-[#0e0e16] border border-gray-800/40 hover:bg-[#12121a] hover:border-purple-500 cursor-pointer transition-all group relative">
        <div className="w-1 h-6 rounded-full" style={{backgroundColor: driver.teamColor}}></div>
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
                <div className="text-sm font-bold text-white truncate group-hover:text-purple-300 transition-colors">{driver.username}</div>
                {attendance === 'ATTENDING' && <div title="Angemeldet" className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.6)]"></div>}
                {attendance === 'ABSENT' && <div title="Abgemeldet" className="text-red-500"><XCircle size={10}/></div>}
            </div>
            <div className="text-[10px] text-gray-500 uppercase">{driver.teamName}</div>
        </div>
        <ChevronRight size={14} className="text-gray-600 group-hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"/>
    </div>
);

const PredictionDetailModal: React.FC<{
    participant: Participant;
    realResults: RealResult[];
    realSprintResults: RealResult[];
    drivers: any[]; 
    pointsConfig: { exact: number, top10: number, bonus: number, sprint_exact: number, sprint_top3: number };
    onClose: () => void;
}> = ({ participant, realResults, realSprintResults, drivers, pointsConfig, onClose }) => {
    
    const [viewingSprint, setViewingSprint] = useState(false);
    
    const calculateForPicks = (picks: (string | null)[], results: any[], isSprint: boolean) => {
        let score = 0;
        let exact = 0;
        let top10 = 0;
        picks.forEach((pid, idx) => {
            if(!pid) return;
            const res = results.find(r => r.user_id === pid);
            const actualPos = res?.position || res?.sprint_position;
            if (actualPos === idx + 1) { score += isSprint ? pointsConfig.sprint_exact : pointsConfig.exact; exact++; }
            else if (actualPos && actualPos <= 10) { score += isSprint ? pointsConfig.sprint_top3 : pointsConfig.top10; top10++; }
        });
        if (exact === 10) score += pointsConfig.bonus;
        return { score, exact, top10 };
    };

    const raceStats = calculateForPicks(participant.picks || [], realResults, false);
    const sprintStats = calculateForPicks(participant.sprint_picks || [], realSprintResults, true);
    
    const activeStats = viewingSprint ? sprintStats : raceStats;
    const activePicks = viewingSprint ? (participant.sprint_picks || []) : (participant.picks || []);
    const activeRealResults = viewingSprint ? realSprintResults : realResults;
    
    const totalCalculated = raceStats.score + sprintStats.score;
    const displayPoints = participant.last_points !== undefined ? participant.last_points : totalCalculated;
    
    const hasSprintPicks = participant.sprint_picks && participant.sprint_picks.some(p => p !== null);
    
    return (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-[#1a1a20] border border-purple-500/50 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                
                <div className="p-4 bg-[#0e0e16] border-b border-gray-800/40 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-800 border-2 border-gray-600 flex items-center justify-center overflow-hidden">
                             {participant.avatar_url ? <img src={participant.avatar_url} className="w-full h-full object-cover"/> : <span className="font-bold text-gray-500">{participant.username.charAt(0)}</span>}
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white leading-none">{participant.username}</h3>
                            <div className="text-xs text-gray-400">{participant.team_name}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={24}/></button>
                </div>

                <div className="p-3 bg-purple-900/10 border-b border-purple-500/20 text-center flex justify-around items-center">
                     <div>
                         <div className="text-3xl font-black text-white">{displayPoints} <span className="text-sm font-bold text-purple-400 uppercase">PTS</span></div>
                         <div className="text-[10px] text-gray-500 uppercase font-bold">Total Score</div>
                     </div>
                     <div className="text-left text-xs text-gray-400 border-l border-gray-700 pl-4">
                         <div className="text-white font-bold mb-1">{viewingSprint ? 'Sprint' : 'Rennen'} ({activeStats.score} Pkt)</div>
                         <div>Exakt: <span className="text-green-400 font-bold">{activeStats.exact}</span></div>
                         <div>Top 10: <span className="text-yellow-500 font-bold">{activeStats.top10}</span></div>
                     </div>
                </div>
                
                {hasSprintPicks && (
                    <div className="flex bg-gray-900 p-1 border-b border-gray-700">
                        <button 
                            onClick={() => setViewingSprint(false)} 
                            className={`flex-1 py-1.5 text-xs font-bold uppercase rounded transition-colors ${!viewingSprint ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                        >
                            Hauptrennen
                        </button>
                        <button 
                            onClick={() => setViewingSprint(true)} 
                            className={`flex-1 py-1.5 text-xs font-bold uppercase rounded transition-colors ${viewingSprint ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                        >
                            Sprint
                        </button>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                    {activePicks.map((driverId, idx) => {
                        if (!driverId) {
                            return (
                                <div key={idx} className="flex items-center p-2 rounded border border-gray-800 bg-gray-900/30 opacity-50">
                                    <div className="w-8 h-8 flex items-center justify-center font-black text-sm rounded bg-black/40 mr-3 text-gray-600">{idx+1}</div>
                                    <div className="text-xs text-gray-500 italic">Kein Tipp</div>
                                </div>
                            );
                        }
                        
                        const driver = drivers.find(d => d.id === driverId);
                        const actualPosOfPick = activeRealResults.find(r => r.user_id === driverId)?.position || activeRealResults.find(r => r.user_id === driverId)?.sprint_position;
                        
                        let status = 'MISS';
                        let pts = 0;
                        let statusText = 'Falsch';
                        let statusColor = 'border-gray-700 bg-[#0e0e16]/40 opacity-60';

                        if (activeRealResults.length > 0) { 
                            if (actualPosOfPick === idx + 1) {
                                status = 'EXACT';
                                pts = viewingSprint ? pointsConfig.sprint_exact : pointsConfig.exact;
                                statusText = 'Exakt';
                                statusColor = 'border-green-500 bg-green-900/20 shadow-[0_0_10px_rgba(34,197,94,0.2)]';
                            } else if (actualPosOfPick && actualPosOfPick <= 10) {
                                status = 'TOP10';
                                pts = viewingSprint ? pointsConfig.sprint_top3 : pointsConfig.top10;
                                statusText = `P${actualPosOfPick}`;
                                statusColor = 'border-yellow-500/50 bg-yellow-900/10';
                            } else {
                                statusText = actualPosOfPick ? `P${actualPosOfPick}` : 'DNF/Out';
                            }
                        } else {
                            statusText = '-';
                        }
                        
                        return (
                            <div key={idx} className={`flex items-center p-2 rounded border ${statusColor}`}>
                                <div className={`w-8 h-8 flex items-center justify-center font-black text-sm rounded bg-black/40 mr-3 ${status === 'EXACT' ? 'text-green-400' : 'text-gray-500'}`}>
                                    {idx + 1}
                                </div>
                                <div className="flex-1">
                                    <div className="font-bold text-white text-sm">{driver?.username || 'Unknown'}</div>
                                    <div className="text-[10px] text-gray-500 uppercase">{driver?.teamName}</div>
                                </div>
                                <div className="text-right">
                                    <div className={`text-xs font-bold ${status === 'EXACT' ? 'text-green-400' : status === 'TOP10' ? 'text-yellow-500' : 'text-gray-600'}`}>
                                        {status === 'EXACT' ? <CheckCircle size={14} className="inline mr-1"/> : null}
                                        {activeRealResults.length > 0 ? (pts > 0 ? `+${pts}` : '0') : ''}
                                    </div>
                                    <div className="text-[9px] text-gray-600">{statusText}</div>
                                </div>
                            </div>
                        )
                    })}
                </div>
                
                <div className="p-3 bg-[#0e0e16] border-t border-gray-800/40 text-xs text-gray-500 text-center">
                    Bonus für 10 Richtige: +{pointsConfig.bonus} Pkt
                </div>
            </div>
        </div>
    );
};

const DebugConsole: React.FC<{ userId?: string, eventId?: string }> = ({ userId, eventId }) => {
    const [output, setOutput] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    
    const runCheck = async (mode: string) => {
        setLoading(true);
        setOutput(null);
        try {
            let url = `predictions.php?mode=${mode}&_t=${Date.now()}`;
            if (userId) url += `&userId=${userId}`;
            if (eventId) url += `&eventId=${eventId}`;
            
            const res = await fetch(`${API_BASE_URL}/${url}`);
            const text = await res.text();
            
            try {
                const json = JSON.parse(text);
                setOutput(json);
            } catch {
                setOutput({ error: "Invalid JSON", raw: text });
            }
        } catch(e: any) {
            setOutput({ error: e.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <div className="mb-4 space-x-2">
                <Button onClick={() => runCheck('debug_info')} disabled={loading}>DB & Tables Check</Button>
                <Button onClick={() => runCheck('participants')} disabled={loading || !eventId} variant="secondary">Raw Participants</Button>
                <Button onClick={() => runCheck('logs')} disabled={loading} variant="secondary">Raw Logs</Button>
            </div>
            <div className="bg-black p-4 rounded border border-gray-700 overflow-auto max-h-[500px] text-xs font-mono text-green-400">
                {loading ? "Loading..." : <pre>{JSON.stringify(output, null, 2)}</pre>}
            </div>
        </Card>
    );
};

export const PredictionGame: React.FC = () => {
    const { user } = useAuth();
    const { drivers, settings, teams } = useLeague();
    
    // Core State
    const [events, setEvents] = useState<any[]>([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [currentPicks, setCurrentPicks] = useState<(string | null)[]>(Array(10).fill(null));
    const [currentSprintPicks, setCurrentSprintPicks] = useState<(string | null)[]>(Array(10).fill(null));
    const [points, setPoints] = useState<number | null>(null);
    const [sprintPoints, setSprintPoints] = useState<number | null>(null);
    const [isLocked, setIsLocked] = useState(false);
    const [saving, setSaving] = useState(false);
    const [predictingSprint, setPredictingSprint] = useState(false);
    
    // UI State
    const [activeTab, setActiveTab] = useState<'GAME' | 'RESULTS' | 'LEADERBOARD' | 'HISTORY' | 'LOGS' | 'DEBUG'>('GAME');
    const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [realResults, setRealResults] = useState<RealResult[]>([]); 
    const [realSprintResults, setRealSprintResults] = useState<RealResult[]>([]); 
    const [detailsUnlocked, setDetailsUnlocked] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [logs, setLogs] = useState<LogItem[]>([]);
    
    // Results Tab Logic
    const [resultsEventId, setResultsEventId] = useState('');
    const [resultsParticipants, setResultsParticipants] = useState<Participant[]>([]);
    const [resultsReal, setResultsReal] = useState<RealResult[]>([]);
    const [resultsSprintReal, setResultsSprintReal] = useState<RealResult[]>([]);
    const [resultsViewingSprint, setResultsViewingSprint] = useState(false);
    
    const [poolSearch, setPoolSearch] = useState('');
    const [activeSlot, setActiveSlot] = useState<number | null>(null);
    const [loadingParts, setLoadingParts] = useState(false);
    
    const [viewingParticipant, setViewingParticipant] = useState<Participant | null>(null);
    const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null);
    const [attendanceMap, setAttendanceMap] = useState<Record<string, string>>({});

    const isWebAdmin = user?.roles.includes(UserRole.WEBADMIN) || user?.roles.includes(UserRole.ADMIN);

    // Initial Load
    useEffect(() => {
        // Always force fresh load with timestamp
        apiRequest<any[]>(`events.php?_t=${Date.now()}`).then(data => {
            const upc = data.filter((e: any) => !e.isTestRace).sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
            setEvents(upc);
            
            const nextOpen = upc.find(e => e.status !== 'COMPLETED' && e.status !== 'REGISTRATION_CLOSED');
            if (nextOpen) {
                setSelectedEventId(nextOpen.id);
            } else if (upc.length > 0) {
                setSelectedEventId(upc[upc.length-1].id);
            }
            
            // For Results Tab: Default to last completed
            const lastCompleted = [...upc].reverse().find(e => e.status === 'COMPLETED');
            if (lastCompleted) setResultsEventId(lastCompleted.id);
        });
        
        apiRequest<LeaderboardEntry[]>(`predictions.php?mode=leaderboard&_t=${Date.now()}`)
            .then(res => setLeaderboard(Array.isArray(res) ? res : []))
            .catch(()=>{});
            
        if (user) {
            apiRequest<HistoryItem[]>(`predictions.php?mode=history&userId=${user.id}&_t=${Date.now()}`)
                .then(res => setHistory(Array.isArray(res) ? res : []))
                .catch(()=>{});
        }
    }, [user]);

    // Load Picks, Participants and Attendance when Event/User changes (MAIN GAME TAB)
    useEffect(() => {
        let active = true;

        if (selectedEventId && user) {
            const fetchAll = async () => {
                // 1. Prediction (Using basic apiRequest is fine here, assuming it unwraps 'data')
                try {
                    const res = await apiRequest<Prediction>(`predictions.php?eventId=${selectedEventId}&userId=${user?.id}&_t=${Date.now()}`);
                    if (active) {
                        if (res && res.picks) {
                            const picks = res.picks || Array(10).fill(null);
                            while (picks.length < 10) picks.push(null);
                            setCurrentPicks(picks);
                            
                            const sprintPicks = res.sprint_picks || Array(10).fill(null);
                            while (sprintPicks.length < 10) sprintPicks.push(null);
                            setCurrentSprintPicks(sprintPicks);
                            
                            setPoints(res.points);
                            setSprintPoints(res.sprint_points);
                        } else {
                            setCurrentPicks(Array(10).fill(null));
                            setCurrentSprintPicks(Array(10).fill(null));
                            setPoints(null);
                            setSprintPoints(null);
                        }
                    }
                } catch(e) {}
                
                // 2. Participants - FIX: USE apiRequestFull to get siblings like real_results
                setLoadingParts(true);
                setFetchError(null);
                try {
                    const res = await apiRequestFull<any>(`predictions.php?mode=participants&eventId=${selectedEventId}&userId=${user?.id}&_t=${Date.now()}`);
                    if (active) {
                        if (res.status === 'success' && res.data) {
                            // apiRequestFull gives us the raw response object (including status, data, real_results)
                            const fullResponse = res as any; 
                            setParticipants(Array.isArray(fullResponse.data) ? fullResponse.data : []);
                            setRealResults(fullResponse.real_results || []);
                            setRealSprintResults(fullResponse.real_sprint_results || []);
                            setDetailsUnlocked(!!fullResponse.details_unlocked);
                        } else {
                           setParticipants([]);
                           setFetchError(res.message || "Unbekannter Fehler");
                        }
                    }
                } catch(e: any) {
                    if (active) setFetchError(e.message);
                } finally {
                    if (active) setLoadingParts(false);
                }

                // 3. Attendance
                try {
                    const res = await apiRequest<any[]>(`attendance.php?eventId=${selectedEventId}&_t=${Date.now()}`);
                    if (active && Array.isArray(res)) {
                        const map: Record<string, string> = {};
                        res.forEach(item => { if (item.user_id) map[item.user_id] = item.status; });
                        setAttendanceMap(map);
                    }
                } catch(e) {}
            };

            fetchAll();
            
            const evt = events.find(e => e.id === selectedEventId);
            if (evt) {
                const start = new Date(evt.date).getTime();
                const lockedByStatus = evt.status === 'COMPLETED' || evt.status === 'REGISTRATION_CLOSED'; 
                const lockedByTime = Date.now() > start;
                setIsLocked(lockedByStatus || lockedByTime);
            }
        }
        
        return () => { active = false; };
    }, [selectedEventId, user, events]);

    // Force reload on tab change
    useEffect(() => {
        if (activeTab === 'LOGS' && user) {
             apiRequest<LogItem[]>(`predictions.php?mode=logs&userId=${user.id}&_t=${Date.now()}`)
                .then(res => setLogs(Array.isArray(res) ? res : []))
                .catch(console.error);
        }
        if (activeTab === 'RESULTS' && resultsEventId) {
             // FIX: Use apiRequestFull here too for Results Tab
             apiRequestFull<any>(`predictions.php?mode=participants&eventId=${resultsEventId}&_t=${Date.now()}`)
                .then(res => {
                    const full = res as any;
                    setResultsParticipants(Array.isArray(full.data) ? full.data : []);
                    setResultsReal(full.real_results || []);
                    setResultsSprintReal(full.real_sprint_results || []);
                })
                .catch(() => setResultsParticipants([]));
        }
    }, [activeTab, user, resultsEventId]);

    // MANUAL REFRESH BUTTON
    const triggerLoadParticipants = async () => {
        if (!selectedEventId) return;
        setLoadingParts(true);
        setFetchError(null);
        try {
            // FIX: Use apiRequestFull
            const res = await apiRequestFull<any>(`predictions.php?mode=participants&eventId=${selectedEventId}&userId=${user?.id}&_t=${Date.now()}`);
            const full = res as any;
            if (res.status === 'success') {
                setParticipants(Array.isArray(full.data) ? full.data : []);
                setRealResults(full.real_results || []);
                setRealSprintResults(full.real_sprint_results || []);
                setDetailsUnlocked(!!full.details_unlocked);
            } else {
                setFetchError(res.message);
            }
        } catch (e: any) {
            setFetchError(e.message);
        } finally {
            setLoadingParts(false);
        }
    };

    const handlePick = (posIndex: number, driverId: string | null) => {
        if (isLocked) return;
        if (predictingSprint) {
            const newPicks = [...currentSprintPicks];
            if (driverId) {
                const existingIdx = newPicks.indexOf(driverId);
                if (existingIdx !== -1) newPicks[existingIdx] = null;
            }
            newPicks[posIndex] = driverId;
            setCurrentSprintPicks(newPicks);
        } else {
            const newPicks = [...currentPicks];
            if (driverId) {
                const existingIdx = newPicks.indexOf(driverId);
                if (existingIdx !== -1) newPicks[existingIdx] = null;
            }
            newPicks[posIndex] = driverId;
            setCurrentPicks(newPicks);
        }
    };

    const fillNextSlot = (driverId: string) => {
        if (isLocked) return;
        if (activeSlot !== null) {
            handlePick(activeSlot, driverId);
            setActiveSlot(null); 
            return;
        }
        const picksArray = predictingSprint ? currentSprintPicks : currentPicks;
        const firstEmpty = picksArray.findIndex(p => p === null);
        if (firstEmpty !== -1) {
            handlePick(firstEmpty, driverId);
        }
    };
    
    const handleSlotClick = (index: number) => {
        if (isLocked) return;
        if (activeSlot === index) {
            setActiveSlot(null);
            return;
        }
        if (activeSlot !== null) {
            if (predictingSprint) {
                const newPicks = [...currentSprintPicks];
                const temp = newPicks[activeSlot];
                newPicks[activeSlot] = newPicks[index];
                newPicks[index] = temp;
                setCurrentSprintPicks(newPicks);
            } else {
                const newPicks = [...currentPicks];
                const temp = newPicks[activeSlot];
                newPicks[activeSlot] = newPicks[index];
                newPicks[index] = temp;
                setCurrentPicks(newPicks);
            }
            setActiveSlot(null); 
        } else {
            setActiveSlot(index);
        }
    };
    
    const moveSlot = (index: number, direction: 'up' | 'down') => {
        if (isLocked) return;
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= 10) return;
        
        if (predictingSprint) {
            const newPicks = [...currentSprintPicks];
            const temp = newPicks[index];
            newPicks[index] = newPicks[targetIndex];
            newPicks[targetIndex] = temp;
            setCurrentSprintPicks(newPicks);
        } else {
            const newPicks = [...currentPicks];
            const temp = newPicks[index];
            newPicks[index] = newPicks[targetIndex];
            newPicks[targetIndex] = temp;
            setCurrentPicks(newPicks);
        }
        if (activeSlot === index) setActiveSlot(targetIndex);
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        if (isLocked) return;
        setDragSourceIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (isLocked) return;
        e.preventDefault(); 
    };

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        if (isLocked || dragSourceIndex === null) return;
        e.preventDefault();
        if (predictingSprint) {
            const newPicks = [...currentSprintPicks];
            const temp = newPicks[dragSourceIndex];
            newPicks[dragSourceIndex] = newPicks[targetIndex];
            newPicks[targetIndex] = temp;
            setCurrentSprintPicks(newPicks);
        } else {
            const newPicks = [...currentPicks];
            const temp = newPicks[dragSourceIndex];
            newPicks[dragSourceIndex] = newPicks[targetIndex];
            newPicks[targetIndex] = temp;
            setCurrentPicks(newPicks);
        }
        setDragSourceIndex(null);
    };

    const handleSave = async () => {
        const selectedEvent = events.find(e => e.id === selectedEventId);
        
        if (currentPicks.includes(null)) {
            if(!confirm("Du hast nicht alle 10 Plätze für das Hauptrennen belegt. Trotzdem speichern?")) return;
        }
        if (selectedEvent?.isSprint && currentSprintPicks.includes(null)) {
            if(!confirm("Du hast nicht alle 10 Plätze für den Sprint belegt. Trotzdem speichern?")) return;
        }

        setSaving(true);
        try {
            await apiRequest('predictions.php', 'POST', {
                userId: user?.id,
                eventId: selectedEventId,
                picks: currentPicks,
                sprint_picks: currentSprintPicks
            });
            alert("Tipp erfolgreich gespeichert!");
            
            // Re-fetch immediately to update the list view
            triggerLoadParticipants();
            
            apiRequest<HistoryItem[]>(`predictions.php?mode=history&userId=${user?.id}&_t=${Date.now()}`)
                .then(res => setHistory(Array.isArray(res) ? res : []))
                .catch(()=>{});
            
        } catch (e: any) {
            alert("Fehler beim Speichern: " + e.message);
        } finally {
            setSaving(false);
        }
    };

    const getUrl = (url?: string) => {
        if (!url) return undefined;
        if (url.startsWith('http') || url.startsWith('data:')) return url;
        const baseUrl = API_BASE_URL === '.' ? '' : API_BASE_URL;
        return baseUrl ? `${baseUrl}/${url}` : url;
    };

    const activePicksArray = predictingSprint ? currentSprintPicks : currentPicks;
    const availableDrivers = drivers.filter(d => 
        d.seasonDriverId && 
        (!activePicksArray.includes(d.id)) &&
        (d.username.toLowerCase().includes(poolSearch.toLowerCase()) || (d.teamName || '').toLowerCase().includes(poolSearch.toLowerCase()))
    ).sort((a,b) => {
        const aAtt = attendanceMap[a.id] === 'ATTENDING';
        const bAtt = attendanceMap[b.id] === 'ATTENDING';
        if (aAtt && !bAtt) return -1;
        if (!aAtt && bAtt) return 1;
        return (a.teamName || '').localeCompare(b.teamName || '');
    });

    const getDriverName = (id: string | null) => {
        if (!id) return null;
        const d = drivers.find(drv => drv.id === id);
        return d ? d.username : 'Unknown';
    };
    
    const pointsConfig = {
        exact: settings.prediction_points_exact || 10,
        top10: settings.prediction_points_top10 || 1,
        bonus: settings.prediction_points_full_bonus || 25,
        sprint_exact: settings.prediction_points_sprint_exact || 5,
        sprint_top3: settings.prediction_points_sprint_top3 || 1
    };

    const completedEvents = events.filter(e => e.status === 'COMPLETED');

    return (
        <div className="sdc-feature-page sdc-predictions-page max-w-7xl mx-auto space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Race Prediction"
                title="Tippspiel"
                description="Stelle deine Top 10 zusammen, sammle Punkte für präzise Vorhersagen und miss dich über die gesamte Saison."
                icon={Trophy}
                accent="#a855f7"
                stats={[
                    { label: 'Rennen', value: events.length },
                    { label: 'Gewertet', value: completedEvents.length },
                    { label: 'Fahrer', value: drivers.filter(d => d.seasonDriverId).length },
                ]}
                actions={(
                    <div className="sdc-feature-tabs w-full md:w-auto">
                        <button onClick={() => setActiveTab('GAME')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab==='GAME' ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20' : 'text-gray-600 hover:text-gray-400'}`}><GripVertical size={13}/> Tippen</button>
                        <button onClick={() => setActiveTab('RESULTS')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab==='RESULTS' ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20' : 'text-gray-600 hover:text-gray-400'}`}><LayoutGrid size={13}/> Ergebnisse</button>
                        <button onClick={() => setActiveTab('LEADERBOARD')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab==='LEADERBOARD' ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20' : 'text-gray-600 hover:text-gray-400'}`}><List size={13}/> Rangliste</button>
                        <button onClick={() => setActiveTab('HISTORY')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab==='HISTORY' ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20' : 'text-gray-600 hover:text-gray-400'}`}><History size={13}/> Historie</button>
                        {isWebAdmin && <button onClick={() => setActiveTab('LOGS')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab==='LOGS' ? 'bg-f1-red text-white' : 'text-gray-600 hover:text-gray-400'}`}><Terminal size={13}/> Konsole</button>}
                        {isWebAdmin && <button onClick={() => setActiveTab('DEBUG')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-2 transition-all ${activeTab==='DEBUG' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-400'}`}><Bug size={13}/> Debug</button>}
                    </div>
                )}
            />

            {viewingParticipant && (
                <PredictionDetailModal 
                    participant={viewingParticipant} 
                    realResults={activeTab === 'RESULTS' ? resultsReal : realResults}
                    realSprintResults={activeTab === 'RESULTS' ? resultsSprintReal : realSprintResults}
                    drivers={drivers}
                    pointsConfig={pointsConfig}
                    onClose={() => setViewingParticipant(null)} 
                />
            )}

            {activeTab === 'DEBUG' && isWebAdmin && (
                <DebugConsole userId={user?.id} eventId={selectedEventId} />
            )}

            {activeTab === 'GAME' && (
                <>
                    <Card className="mb-6 rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
                        <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                            <div className="w-full md:w-auto flex-1">
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Event wählen</label>
                                <select 
                                    className="bg-[#0e0e16] border border-gray-800/40 rounded p-2 text-white font-bold w-full md:min-w-[300px]"
                                    value={selectedEventId}
                                    onChange={e => setSelectedEventId(e.target.value)}
                                >
                                    {events.map(e => (
                                        <option key={e.id} value={e.id}>{e.trackLocation} - R{e.roundNumber} {e.status === 'COMPLETED' ? '(Fertig)' : ''}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                                {points !== null && (
                                    <div className="text-center bg-purple-900/30 px-4 py-2 rounded border border-purple-500/50">
                                        <div className="text-xs text-purple-300 font-bold uppercase">Dein Score</div>
                                        <div className="text-2xl font-black text-white">{(points || 0) + (sprintPoints || 0)} Pkt</div>
                                    </div>
                                )}
                                
                                {isLocked ? (
                                    <div className="flex items-center gap-2 text-red-400 bg-red-900/10 px-3 py-1 rounded border border-red-900/30">
                                        <Lock size={16}/> <span className="font-bold text-sm">Geschlossen</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-green-400 bg-green-900/10 px-3 py-1 rounded border border-green-900/30 animate-pulse">
                                        <Unlock size={16}/> <span className="font-bold text-sm">Offen</span>
                                    </div>
                                )}

                                {!isLocked && (
                                    <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-500 border-none shadow-lg">
                                        {saving ? <RefreshCw className="animate-spin"/> : <Save/>} Speichern
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-gray-700">
                             <div className="flex items-center justify-between mb-3">
                                 <div className="flex items-center gap-2">
                                     <Users className="text-purple-400" size={18} />
                                     <h3 className="font-bold text-white uppercase text-sm">
                                         Teilnehmer-Übersicht ({participants.length})
                                     </h3>
                                     {/* DEBUG OUTPUT */}
                                     <div className="text-[10px] text-gray-600 font-mono hidden md:block">
                                         ID: {selectedEventId || 'none'}
                                     </div>
                                 </div>
                                 <button onClick={triggerLoadParticipants} className="text-gray-500 hover:text-white p-1" title="Aktualisieren">
                                     <RefreshCw size={14} className={loadingParts ? "animate-spin" : ""}/>
                                 </button>
                             </div>
                             
                             {fetchError && (
                                <div className="bg-red-900/20 border border-red-500 text-red-300 p-2 rounded text-xs mb-2 flex items-center gap-2">
                                    <AlertTriangle size={14}/>
                                    {fetchError} (Klicke auf Refresh)
                                </div>
                             )}

                             {participants.length === 0 ? (
                                 <div className="text-sm text-gray-500 italic p-3 bg-[#0e0e16]/60 rounded border border-gray-800/30 flex items-center gap-2">
                                     <Info size={16}/> Noch keine Tipps für dieses Rennen eingegangen.
                                 </div>
                             ) : (
                                 <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                                     {participants.map((p, idx) => {
                                         const isMe = p.username === user?.username;
                                         return (
                                             <div 
                                                 key={idx} 
                                                 className={`flex items-center gap-2 bg-[#0e0e16] border border-gray-800/40 p-2 rounded ${detailsUnlocked ? 'cursor-pointer hover:border-purple-500 hover:bg-gray-800' : ''}`}
                                                 onClick={() => detailsUnlocked && setViewingParticipant(p)}
                                                 title={detailsUnlocked ? "Klicken für Details" : "Details noch gesperrt"}
                                             >
                                                 <div className="relative">
                                                     <div className="w-8 h-8 rounded-full border-2 border-gray-600 bg-gray-800 overflow-hidden flex items-center justify-center shrink-0" style={{borderColor: p.team_color || '#666'}}>
                                                         {p.avatar_url ? (
                                                             <img src={getUrl(p.avatar_url)} className="w-full h-full object-cover" />
                                                         ) : (
                                                             <span className="text-xs font-bold text-gray-400">{p.username.substring(0,1)}</span>
                                                         )}
                                                     </div>
                                                     {isMe && <div className="absolute -bottom-1 -right-1 bg-purple-600 rounded-full w-3 h-3 border border-black"></div>}
                                                 </div>
                                                 <div className="min-w-0 flex-1">
                                                     <div className={`text-xs font-bold truncate ${isMe ? 'text-purple-400' : 'text-gray-300'}`}>
                                                         {p.username}
                                                     </div>
                                                     {p.last_points !== null && p.last_points !== undefined && (
                                                         <div className="text-[9px] text-gray-400 font-mono flex items-center gap-1">
                                                             <span>{p.last_points} Pkt</span>
                                                             {p.sprint_points !== null && p.sprint_points !== undefined && p.sprint_points > 0 && (
                                                                 <span className="text-[8px] text-gray-500">({p.points || 0}+{p.sprint_points}S)</span>
                                                             )}
                                                         </div>
                                                     )}
                                                 </div>
                                                 {detailsUnlocked && <Eye size={12} className="text-gray-600"/>}
                                             </div>
                                         )
                                     })}
                                 </div>
                             )}
                        </div>
                    </Card>
                    
                    {/* Rest of Game UI (Picks, etc.) */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1 space-y-3">
                            <div className="flex justify-between items-end border-b border-gray-800 pb-2 mb-2">
                                <h3 className="text-white font-bold uppercase text-sm">
                                    Deine Tipps
                                </h3>
                                <span className="text-[10px] text-gray-500">Ziehen zum Sortieren / Tippen zum Tauschen</span>
                            </div>
                            
                            {events.find(e => e.id === selectedEventId)?.isSprint && (
                                <div className="flex bg-[#0e0e16] rounded-lg p-1 mb-4 border border-gray-800/40">
                                    <button 
                                        onClick={() => setPredictingSprint(false)} 
                                        className={`flex-1 py-1.5 text-xs font-bold uppercase rounded transition-colors ${!predictingSprint ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                                    >
                                        Hauptrennen
                                    </button>
                                    <button 
                                        onClick={() => setPredictingSprint(true)} 
                                        className={`flex-1 py-1.5 text-xs font-bold uppercase rounded transition-colors ${predictingSprint ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                                    >
                                        Sprint
                                    </button>
                                </div>
                            )}

                            {(predictingSprint ? currentSprintPicks : currentPicks).map((pickId, idx) => {
                                const driver = drivers.find(d => d.id === pickId);
                                const isFilled = !!driver;
                                const isActive = activeSlot === idx;

                                return (
                                    <div 
                                        key={idx} 
                                        className={`relative flex items-center bg-[#12121a] border rounded-lg overflow-hidden transition-all duration-200 cursor-pointer ${isActive ? 'border-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.3)] bg-purple-900/10' : 'border-gray-800 hover:border-gray-600'} ${!isLocked ? 'cursor-pointer' : ''}`}
                                        onClick={() => handleSlotClick(idx)}
                                        draggable={!isLocked}
                                        onDragStart={(e) => handleDragStart(e, idx)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, idx)}
                                        style={{ opacity: dragSourceIndex === idx ? 0.4 : 1 }}
                                    >
                                        <div className={`w-10 flex items-center justify-center font-black text-lg ${idx===0 ? 'text-yellow-400 bg-yellow-400/10 h-14' : idx===1 ? 'text-gray-300 bg-gray-300/10 h-12' : idx===2 ? 'text-orange-400 bg-orange-400/10 h-12' : 'text-gray-500 h-10'}`}>
                                            {idx + 1}
                                        </div>
                                        
                                        <div className="flex-1 px-3 flex items-center justify-between">
                                            {!isLocked && (
                                                <div className="mr-2 text-gray-700 hidden md:block" title="Drag Handle">
                                                    <GripVertical size={16}/>
                                                </div>
                                            )}
                                            {isFilled ? (
                                                <div className="flex items-center gap-3 w-full">
                                                    <div className="w-1 h-6 rounded-full" style={{backgroundColor: driver?.teamColor}}></div>
                                                    <div className="min-w-0">
                                                        <div className="font-bold text-white text-sm truncate">{driver?.username}</div>
                                                        <div className="text-[9px] text-gray-500 uppercase truncate">{driver?.teamName}</div>
                                                    </div>
                                                    
                                                    {!isLocked && (
                                                        <div className="ml-auto flex items-center gap-1">
                                                            <div className="flex flex-col gap-0.5 mr-2 md:hidden">
                                                                <button onClick={(e) => {e.stopPropagation(); moveSlot(idx, 'up')}} disabled={idx === 0} className="p-1 bg-gray-800 rounded hover:bg-purple-600 disabled:opacity-20"><ArrowUp size={10}/></button>
                                                                <button onClick={(e) => {e.stopPropagation(); moveSlot(idx, 'down')}} disabled={idx === 9} className="p-1 bg-gray-800 rounded hover:bg-purple-600 disabled:opacity-20"><ArrowDown size={10}/></button>
                                                            </div>

                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); handlePick(idx, null); }}
                                                                className="p-1.5 text-gray-500 hover:text-red-500 transition-colors rounded hover:bg-gray-800"
                                                                title="Entfernen"
                                                            >
                                                                <X size={14}/>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-gray-600 text-xs italic w-full">
                                                    {isLocked ? '---' : isActive ? 'Wähle einen Fahrer...' : 'Slot antippen zum Füllen'}
                                                </div>
                                            )}
                                        </div>
                                        {isActive && !isLocked && <div className="absolute right-0 top-0 bottom-0 w-1 bg-purple-500 animate-pulse"></div>}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="lg:col-span-2">
                            <div className="bg-[#1e1e24] border border-gray-700 rounded-xl p-4 sticky top-4 h-[calc(100vh-100px)] flex flex-col">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-bold text-white uppercase text-sm">Verfügbare Fahrer</h3>
                                    <div className="text-xs text-gray-400">{availableDrivers.length} übrig</div>
                                </div>
                                
                                <div className="relative mb-4">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                                    <input 
                                        className="w-full bg-[#0e0e16] border border-gray-800/40 rounded-lg p-2 pl-9 text-sm text-white focus:border-purple-500 outline-none"
                                        placeholder="Fahrer suchen..."
                                        value={poolSearch}
                                        onChange={e => setPoolSearch(e.target.value)}
                                        disabled={isLocked}
                                    />
                                </div>

                                <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                                    {isLocked ? (
                                        <div className="text-center p-10 text-gray-500 italic">
                                            Tippabgabe geschlossen.
                                        </div>
                                    ) : availableDrivers.length === 0 ? (
                                        <div className="text-center p-10 text-gray-500 italic">
                                            Alle Fahrer gewählt!
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {availableDrivers.map(d => (
                                                <DriverPoolItem 
                                                    key={d.id} 
                                                    driver={d} 
                                                    attendance={attendanceMap[d.id]}
                                                    onClick={() => fillNextSlot(d.id)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-2 text-[10px] text-gray-500 flex gap-4 justify-center">
                                    <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500"></div> Angemeldet</span>
                                    <span className="flex items-center gap-1 text-red-500"><XCircle size={10}/> Abgemeldet</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            
            {activeTab === 'RESULTS' && (
                <Card className="overflow-hidden bg-[#12121a] border-gray-800/40">
                    <div className="mb-4 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                        <div className="w-full md:w-auto">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Rennen wählen (Abgeschlossen)</label>
                            <select 
                                className="bg-[#0e0e16] border border-gray-800/40 rounded p-2 text-white font-bold w-full md:min-w-[300px]"
                                value={resultsEventId}
                                onChange={e => setResultsEventId(e.target.value)}
                            >
                                {completedEvents.length === 0 && <option value="">Keine abgeschlossenen Rennen</option>}
                                {completedEvents.map(e => (
                                    <option key={e.id} value={e.id}>{e.trackLocation} (R{e.roundNumber})</option>
                                ))}
                            </select>
                        </div>
                        {events.find(e => e.id === resultsEventId)?.isSprint && (
                            <div className="flex bg-[#0e0e16] rounded-lg p-1 border border-gray-800/40 w-full md:w-auto">
                                <button 
                                    onClick={() => setResultsViewingSprint(false)} 
                                    className={`flex-1 md:px-6 py-1.5 text-xs font-bold uppercase rounded transition-colors ${!resultsViewingSprint ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                                >
                                    Hauptrennen
                                </button>
                                <button 
                                    onClick={() => setResultsViewingSprint(true)} 
                                    className={`flex-1 md:px-6 py-1.5 text-xs font-bold uppercase rounded transition-colors ${resultsViewingSprint ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
                                >
                                    Sprint
                                </button>
                            </div>
                        )}
                    </div>

                    {resultsParticipants.length > 0 && resultsReal.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr>
                                        <th className="p-2 bg-[#0e0e16] border border-gray-800/40 text-white font-bold sticky left-0 z-10 w-32">User</th>
                                        <th className="p-2 bg-[#0e0e16] border border-gray-800/40 text-purple-400 font-bold text-center w-12">PTS</th>
                                        {Array.from({length:10}).map((_, i) => (
                                            <th key={i} className="p-2 bg-[#0e0e16] border border-gray-800/40 text-gray-400 text-center w-24">P{i+1}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {resultsParticipants.map(p => {
                                        const activePicks = resultsViewingSprint ? (p.sprint_picks || []) : (p.picks || []);
                                        const activeRealResults = resultsViewingSprint ? resultsSprintReal : resultsReal;
                                        const displayPoints = resultsViewingSprint ? p.sprint_points : p.points;
                                        return (
                                            <tr key={p.user_id} className="hover:bg-gray-800/50">
                                                <td className="p-2 bg-gray-900/90 border border-gray-700 font-bold text-white sticky left-0 z-10 flex items-center gap-2">
                                                     <div className="w-6 h-6 rounded-full bg-gray-800 overflow-hidden border border-gray-600 flex items-center justify-center shrink-0">
                                                         {p.avatar_url ? <img src={getUrl(p.avatar_url)} className="w-full h-full object-cover"/> : p.username.charAt(0)}
                                                     </div>
                                                     <div className="truncate">{p.username}</div>
                                                </td>
                                                <td className="p-2 border border-gray-700 text-center font-mono font-bold text-purple-400 text-sm">
                                                    {displayPoints !== undefined && displayPoints !== null ? displayPoints : '-'}
                                                </td>
                                                {activePicks.map((pickId, idx) => {
                                                    if (!pickId) return <td key={idx} className="p-2 border border-gray-700 text-center text-gray-600">-</td>;
                                                    
                                                    const driver = drivers.find(d => d.id === pickId);
                                                    const realPos = activeRealResults.find(r => r.user_id === pickId)?.position || activeRealResults.find(r => r.user_id === pickId)?.sprint_position;
                                                    
                                                    let bg = 'bg-transparent';
                                                    let text = 'text-gray-400';
                                                    
                                                    if (realPos === idx + 1) { // Exact
                                                        bg = 'bg-green-900/30'; text = 'text-green-400 font-bold';
                                                    } else if (realPos && realPos <= 10) { // Top 10
                                                        bg = 'bg-yellow-900/10'; text = 'text-yellow-500';
                                                    }
                                                    
                                                    return (
                                                        <td key={idx} className={`p-2 border border-gray-700 text-center ${bg} ${text} truncate max-w-[100px]`} title={driver?.username}>
                                                            {driver?.username || '???'}
                                                            {realPos && realPos !== idx+1 && <span className="text-[9px] ml-1 opacity-50">(P{realPos})</span>}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center p-10 text-gray-500 italic">
                            Wähle ein Rennen, um die Detail-Matrix zu sehen.
                        </div>
                    )}
                </Card>
            )}

            {/* Other Tabs unchanged */}
            {activeTab === 'HISTORY' && (
                <div className="space-y-4">
                    {history.length === 0 ? (
                        <div className="text-center p-10 text-gray-500 border-2 border-dashed border-gray-800 rounded-lg">
                            <History size={48} className="mx-auto mb-4 opacity-20"/>
                            <p>Noch keine vergangenen Tipps gefunden.</p>
                        </div>
                    ) : (
                        history.map(item => (
                            <Card key={item.id} className="border-l-4 border-purple-900/50">
                                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 border-b border-gray-800 pb-2">
                                    <div>
                                        <h3 className="text-lg font-bold text-white">{item.location}</h3>
                                        <div className="text-xs text-gray-400">{item.track_name} • Runde {item.round_number}</div>
                                        <div className="text-[10px] text-gray-500">{new Date(item.date_time).toLocaleDateString()}</div>
                                    </div>
                                    <div className="mt-2 md:mt-0 text-right">
                                        {item.points !== null ? (
                                            <div className="text-2xl font-black text-purple-400">
                                                {(item.points || 0) + (item.sprint_points || 0)} <span className="text-xs text-gray-500 font-bold uppercase">PTS</span>
                                                {item.sprint_points !== undefined && item.sprint_points !== null && (
                                                    <div className="text-[10px] text-gray-400 font-normal">
                                                        (Rennen: {item.points}, Sprint: {item.sprint_points})
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <Badge color="gray">Ausstehend</Badge>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2">
                                    {item.picks.map((pickId, idx) => {
                                        const name = getDriverName(pickId);
                                        return (
                                            <div key={idx} className="bg-gray-900 p-2 rounded border border-gray-800 text-center">
                                                <div className="text-[9px] text-gray-500 font-bold uppercase mb-1">P{idx+1}</div>
                                                <div className="text-xs text-white truncate font-bold">{name || '-'}</div>
                                            </div>
                                        )
                                    })}
                                </div>
                                {item.sprint_picks && item.sprint_picks.some(p => p !== null) && (
                                    <div className="mt-4">
                                        <div className="text-xs text-gray-400 font-bold uppercase mb-2">Sprint Tipps</div>
                                        <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2">
                                            {item.sprint_picks.map((pickId, idx) => {
                                                const name = getDriverName(pickId);
                                                return (
                                                    <div key={idx} className="bg-gray-900 p-2 rounded border border-gray-800 text-center opacity-80">
                                                        <div className="text-[9px] text-gray-500 font-bold uppercase mb-1">P{idx+1}</div>
                                                        <div className="text-xs text-white truncate font-bold">{name || '-'}</div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'LEADERBOARD' && (
                <Card>
                    {leaderboard.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-500 border border-dashed border-gray-800 rounded-lg bg-gray-900/30">
                            <div className="bg-gray-800 p-4 rounded-full mb-4">
                                <Hourglass size={48} className="text-purple-500/50" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2 uppercase">Warte auf Ergebnisse</h3>
                            <p className="text-sm max-w-md text-center">
                                Die Rangliste wird automatisch erstellt, sobald das erste Rennen der Saison beendet und ausgewertet wurde.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-900 text-gray-400 text-xs font-bold uppercase">
                                    <tr>
                                        <th className="p-3 text-center w-16">Rank</th>
                                        <th className="p-3">User</th>
                                        <th className="p-3 text-center">Tipps</th>
                                        <th className="p-3 text-right">Punkte</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800">
                                    {leaderboard.map((entry, idx) => (
                                        <tr key={idx} className={`hover:bg-gray-800/50 ${entry.username === user?.username ? 'bg-purple-900/10' : ''}`}>
                                            <td className="p-3 text-center font-bold text-gray-500">
                                                {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                                            </td>
                                            <td className="p-3 font-bold text-white flex items-center gap-2">
                                                {entry.avatar_url && <img src={getUrl(entry.avatar_url)} className="w-6 h-6 rounded-full object-cover border border-gray-600"/>}
                                                {entry.username}
                                                {entry.username === user?.username && <Badge className="text-[10px] bg-purple-500 text-white">DU</Badge>}
                                            </td>
                                            <td className="p-3 text-center text-gray-400">{entry.tips_count}</td>
                                            <td className="p-3 text-right font-mono font-black text-purple-400 text-lg">{entry.total_score}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            )}
            
            {activeTab === 'LOGS' && isWebAdmin && (
                <Card>
                    <div className="flex items-center gap-2 mb-4 border-b border-gray-700 pb-2">
                        <ShieldAlert className="text-red-500" size={20}/>
                        <h3 className="text-lg font-bold text-white uppercase">Admin Live Log</h3>
                    </div>
                    <div className="overflow-x-auto max-h-[600px]">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-900 text-gray-400 text-xs font-bold uppercase sticky top-0 z-10">
                                <tr>
                                    <th className="p-3">Zeit</th>
                                    <th className="p-3">User</th>
                                    <th className="p-3">Event</th>
                                    <th className="p-3">Aktion</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {logs.map((log, i) => (
                                    <tr key={i} className="hover:bg-gray-800/50">
                                        <td className="p-3 font-mono text-gray-500 text-xs">{formatDateSafe(log.created_at)}</td>
                                        <td className="p-3 font-bold text-white">{log.username}</td>
                                        <td className="p-3 text-gray-300 text-xs">{log.track_location} (R{log.round_number})</td>
                                        <td className="p-3">
                                            <Badge className="bg-blue-900/20 text-blue-400 border-blue-900/50">{log.action}</Badge>
                                        </td>
                                    </tr>
                                ))}
                                {logs.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-gray-500">Keine Logs vorhanden.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
};
